import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import {
  Edit,
  Save,
  Delete,
  PersonAdd,
  Link as LinkIcon,
} from "@mui/icons-material";
import { getItems, addItem, updateItem, deleteItem, getApps } from "../services/api";
import { SubTitle, Title } from "./common/Title";
import { getUserRole } from "../services/getUserRole";
import CustomerEnvironmentList from "./CustomerEnviromentList";

/**
 * Lista de Ambientes do sistema (para admins):
 * - Busca ambientes
 * - Exibe e permite busca
 * - Cria/edita/deleta ambientes
 */
const EnvironmentList = ({ token }) => {
  const [items, setItems] = useState([]);
  const [appsList, setAppsList] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({});
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [role, setUserRole] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testingUrl, setTestingUrl] = useState(false);
  const [openTestDialog, setOpenTestDialog] = useState(false);
  // confirmação de delete
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  // feedback pós-delete
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    let result = getUserRole();
    setUserRole(result);
    fetchApps();
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const data = await getItems(token);
      setItems(data);

      if (data && data[0]) {
        // Initialize shape for "newItem" inputs
        const emptyObject = {};
        Object.keys(data[0]).forEach((key) => {
          emptyObject[key] = "";
        });
        setNewItem(emptyObject);
      }
    } catch (err) {
      setError("Erro ao buscar ambientes. Faça login novamente.");
      console.error(err);
    }
  };

    const fetchApps = async () => {
        try {
          const apps = await getApps(token);
          setAppsList(apps);
        } catch {
          console.error("Erro ao buscar apps");
        }
      };

  const handleEdit = (alias) => {
    setEditingItem(alias);
  };

  const handleSave = async (alias) => {
    try {
      const updatedItem = items.find((item) => item.AliasAccess === alias);
      await updateItem(token, alias, updatedItem);
      setEditingItem(null);
      fetchItems();
    } catch (err) {
      setError("Erro ao atualizar ambiente");
      console.error(err);
    }
  };

  // só abre o diálogo de confirmação
  const handleDeleteClick = (alias) => {
    setItemToDelete(alias);
    setOpenConfirmDelete(true);
  };

  // executa delete após confirmação
  const handleConfirmDelete = async () => {
    try {
      await deleteItem(token, itemToDelete);
      fetchItems();
      setSnackbarSeverity("success");
      setSnackbarMessage("Ambiente deletado com sucesso.");
    } catch {
      setSnackbarSeverity("error");
      setSnackbarMessage("Falha ao deletar ambiente. Tente novamente.");
    } finally {
      setOpenSnackbar(true);
      setOpenConfirmDelete(false);
      setItemToDelete(null);
    }
  };

  // cancela exclusão
  const handleCancelDelete = () => {
    setOpenConfirmDelete(false);
    setItemToDelete(null);
  };

  const handleAdd = async () => {
    try {
      await addItem(token, newItem);
      fetchItems();
    } catch (err) {
      setError("Erro ao adicionar ambiente");
      console.error(err);
    }
  };

  const handleInputChange = (alias, key, value) => {
    setItems((prevItems) =>
      prevItems.map((it) =>
        it.AliasAccess === alias ? { ...it, [key]: value } : it
      )
    );
  };

  const handleNewItemChange = (key, value) => {
    setNewItem((prev) => ({ ...prev, [key]: value }));
  };

  const handleTestConnection = async (alias) => {
    const testUrl = `https://app.rental.itup.com.br/easyhub/default.php?cnpj=${alias}`;
    setTestingUrl(true);
    setOpenTestDialog(true);
    try {
      // Use GET instead of HEAD to retrieve the page content
      const response = await fetch(testUrl);
      const text = await response.text();
      // If the page content is blank or does not contain expected keywords, flag it as a failed connection.
      if (text.trim() === "") {
        setTestResult("❌ Falha na conexão: Página em branco!");
      } else if (
        text.toLowerCase().includes("/api/") ||
        text.toLowerCase().includes("POST")
      ) {
        // Adjust the keywords to those you expect in a successful response.
        setTestResult("✅ Conexão bem-sucedida!");
      } else {
        setTestResult("❌ Falha na conexão: Conteúdo inesperado!");
      }
    } catch (error) {
      console.error("Test connection error:", error);
      setTestResult("❌ Erro ao tentar conectar!");
    }
    setTestingUrl(false);
  };

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Title>🌐 Gestão de Ambientes</Title>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {/* Adicionar novo ambiente */}
      {role === "admin" || role === "user" ? (
        <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <SubTitle>➕ Novo ambiente</SubTitle>
          <Box sx={{ maxHeight: { xs: 300 }, overflowY: "auto", p: 1 }}>
            <Grid container spacing={2}>
              {Object.keys(newItem).map((key) => (
                <Grid item xs={12} sm={6} key={key}>
                  <TextField
                    label={key.replace(/_/g, " ")}
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder={`Digite ${key}...`}
                    value={newItem[key]}
                    onChange={(e) => handleNewItemChange(key, e.target.value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            fullWidth
            startIcon={<PersonAdd />}
            onClick={handleAdd}
          >
            Adicionar ambiente
          </Button>
        </Paper>
      ) : null}
      {role === "admin" || role === "user" ? (
        <>
          {/* Campo de busca */}
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <TextField
              label="🔍 Buscar ambiente"
              variant="outlined"
              size="medium"
              sx={{ width: "40%", minWidth: 300 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>

          {/* Tabela de ambientes */}
          <TableContainer
            component={Paper}
            elevation={3}
            sx={{ maxHeight: "400px", overflowY: "auto" }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                {Object.keys(items[0] || {}).map((key) => (
                  <TableCell key={key}>
                    <b>{key === "app_id" ? "App" : key}</b>
                  </TableCell>
                ))}
                  <TableCell>
                    <b>Ações</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items
                  .filter((item) =>
                    Object.values(item).some((val) =>
                      String(val)
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                    )
                  )
                  .map((item) => {
                    const isEditing = editingItem === item.AliasAccess;
                    return (
                      <TableRow key={item.AliasAccess}>
                        {Object.keys(item).map((key) => (
                         <TableCell key={key}>
                           {key === "app_id" ? (
                              isEditing ? (
                                <FormControl fullWidth size="small">
                                  <InputLabel>App</InputLabel>
                                  <Select
                                    value={item.app_id || ""}
                                    label="App"
                                    onChange={e =>
                                      handleInputChange(
                                        item.AliasAccess,
                                        "app_id",
                                        e.target.value
                                      )
                                   }
                                 >
                                    {appsList.map(a => (
                                      <MenuItem key={a.id} value={a.id}>
                                        {a.app_name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                appsList.find(a => a.id === item.app_id)
                                  ?.app_name || "–"
                              )
                            ) : isEditing && key !== "AliasAccess" ? (
                              <TextField
                                value={item[key]}
                                size="small"
                                onChange={(e) =>
                                  handleInputChange(
                                    item.AliasAccess,
                                    key,
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              /* original rendering for other fields */
                              item[key]
                            )}
                               {/* Se for UrlRest, exibe também o botão de teste do WS */}
                          {!isEditing && key.includes("UrlRest") && item[key] && (
                            <IconButton
                              color="primary"
                              onClick={() => handleTestConnection(item.AliasAccess)}
                              sx={{ ml: 1 }}
                              title="Testar conexão WS"
                            >
                              <LinkIcon />
                            </IconButton>
                          )}
                          </TableCell>
                        ))}
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                          {isEditing ? (
                            <IconButton
                              color="primary"
                              onClick={() => handleSave(item.AliasAccess)}
                            >
                              <Save />
                            </IconButton>
                          ) : (
                            <IconButton
                              color="secondary"
                              onClick={() => handleEdit(item.AliasAccess)}
                            >
                              <Edit />
                            </IconButton>
                          )}
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(item.AliasAccess)}
                          >
                            <Delete />
                          </IconButton>
                              </Box>
                            </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <CustomerEnvironmentList />
      )}
      {/* Diálogo de Teste de Conexão */}
      <Dialog open={openTestDialog} onClose={() => setOpenTestDialog(false)}>
        <DialogTitle>Testando Conexão</DialogTitle>
        <DialogContent>
          {testingUrl ? (
            <CircularProgress />
          ) : (
            <Typography>{testResult}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTestDialog(false)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
       {/* Diálogo de confirmação de exclusão */}
      <Dialog open={openConfirmDelete} onClose={handleCancelDelete}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja deletar o ambiente "{itemToDelete}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Deletar
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar de feedback */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EnvironmentList;
